import api from "@/lib/api";
import { isStudioPreview } from "@/lib/studioPreviewBridge";

// Version du thème PUBLIÉ — mise à jour du site chez un visiteur quand la
// boutique republie son thème depuis le Studio (ou enregistre une modification
// sur le thème déjà en ligne). Le serveur incrémente alors un numéro de version
// (backend/services/theme_version.py).
//
// Le navigateur mémorise la version avec laquelle la page a été affichée.
// Quand la version en ligne est différente, il retélécharge tous les éléments
// et met son cache à jour :
//   * le contenu du cache du service worker est retéléchargé (REFRESH_CACHE,
//     voir public/service-worker.js) ;
//   * la page est rechargée : les données du thème sont redemandées au serveur
//     (dont le cache a été vidé à la publication), et les images du thème ont
//     de nouvelles URL (elles portent la version), donc sont retéléchargées.
//
// QUAND la nouvelle version est détectée :
//   * au retour sur l'onglet (téléphone déverrouillé, changement d'onglet) ;
//   * toutes les 2 minutes tant que la page est ouverte ;
//   * à chaque changement de page dans le site.
//
// QUAND elle est appliquée — sans jamais faire perdre une saisie :
//   * au changement de page, ou au retour sur l'onglet ;
//   * jamais pendant qu'un champ de formulaire a le focus ;
//   * jamais sur les pages où un rechargement dérangerait (tunnel de commande,
//     confirmation, connexion…) : la mise à jour attend la page suivante.
const STORAGE_KEY = "ce_theme_version";
const RELOAD_GUARD_KEY = "ce_theme_reloaded_for";
const CHECK_INTERVAL_MS = 2 * 60 * 1000;
const SW_REFRESH_TIMEOUT_MS = 5000;

const NO_AUTO_RELOAD = [/^\/checkout/, /^\/commande\//, /^\/contact\/confirmation/, /^\/connexion/, /^\/inscription/, /^\/mot-de-passe-oublie/, /^\/reinitialiser-mot-de-passe/, /^\/compte/, /^\/admin/];

let rendered = null; // version avec laquelle la page a été affichée
let pending = null; // version plus récente détectée, pas encore appliquée
let started = false;

const storage = (kind) => {
  try {
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
};
const read = (kind, key) => {
  try {
    return storage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
};
const write = (kind, key, value) => {
  try {
    storage(kind)?.setItem(key, value);
  } catch {
    /* navigation privée, stockage plein : sans conséquence */
  }
};

const isVersion = (v) => typeof v === "number" && Number.isFinite(v);

export function getRenderedThemeVersion() {
  return rendered;
}

export function getPendingThemeVersion() {
  return pending;
}

/**
 * Demande au service worker de retélécharger tout le contenu de son cache.
 * Résout `true` quand c'est fait, `false` sans service worker ou après délai
 * (la mise à jour de la page ne doit jamais rester bloquée par lui).
 */
export function refreshServiceWorkerCache(timeoutMs = SW_REFRESH_TIMEOUT_MS) {
  const sw = typeof navigator !== "undefined" ? navigator.serviceWorker?.controller : null;
  if (!sw || typeof MessageChannel === "undefined") return Promise.resolve(false);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve(false), timeoutMs);
    channel.port1.onmessage = () => {
      window.clearTimeout(timer);
      resolve(true);
    };
    sw.postMessage({ type: "REFRESH_CACHE" }, [channel.port2]);
  });
}

/**
 * À appeler avec la version reçue au démarrage (GET /theme/init). Si le
 * navigateur avait mémorisé une AUTRE version lors d'une visite précédente, la
 * page qui s'affiche est déjà à jour (données redemandées, images aux nouvelles
 * URL) : il ne reste qu'à rafraîchir le cache du service worker.
 */
export function setRenderedThemeVersion(version) {
  if (!isVersion(version) || isStudioPreview()) return;
  if (rendered !== null) return;
  rendered = version;
  const stored = read("local", STORAGE_KEY);
  if (stored !== null && Number(stored) !== version) {
    refreshServiceWorkerCache();
  }
  write("local", STORAGE_KEY, String(version));
}

function canReloadNow() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  if (NO_AUTO_RELOAD.some((re) => re.test(path))) return false;
  const el = document.activeElement;
  if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)) {
    return false;
  }
  return true;
}

/** Applique la version en attente : cache retéléchargé, puis page rechargée. */
export async function applyPendingThemeVersion() {
  if (pending === null || !canReloadNow()) return false;
  // Garde-fou : jamais deux rechargements pour la même version (un serveur
  // qui renverrait encore l'ancienne version au démarrage ne doit pas
  // provoquer une boucle).
  if (read("session", RELOAD_GUARD_KEY) === String(pending)) return false;
  write("session", RELOAD_GUARD_KEY, String(pending));
  write("local", STORAGE_KEY, String(pending));
  await refreshServiceWorkerCache();
  window.location.reload();
  return true;
}

/** Interroge le serveur ; mémorise une version plus récente, et l'applique si demandé. */
export async function checkThemeVersion({ apply = false } = {}) {
  if (rendered === null || isStudioPreview()) return null;
  let latest;
  try {
    const { data } = await api.get("/theme/version");
    latest = data?.theme_version;
  } catch {
    return null;
  }
  if (isVersion(latest) && latest !== rendered) pending = latest;
  if (apply) await applyPendingThemeVersion();
  return latest;
}

/** Changement de page dans le site : moment idéal pour appliquer une mise à jour. */
export function onThemeNavigation() {
  if (rendered === null || isStudioPreview()) return;
  if (pending !== null) applyPendingThemeVersion();
  else checkThemeVersion({ apply: true });
}

/** Lance la surveillance (une seule fois, jamais dans l'aperçu du Studio). */
export function startThemeVersionWatch() {
  if (started || typeof window === "undefined" || isStudioPreview()) return;
  started = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkThemeVersion({ apply: true });
  });
  window.setInterval(() => checkThemeVersion(), CHECK_INTERVAL_MS);
}

// Tests uniquement.
export function __resetThemeVersionForTests() {
  rendered = null;
  pending = null;
  started = false;
}
